import { useDates } from "../../../dates/DatesContext";
import { useFetch } from "../../../hooks/api";
import moment from "moment";
import { useMemo } from "react";
import { formatDatamartFilters } from "../../../utils/formatV2DatamartFilters";
import { mergeTrendData } from "../../../utils/mergeTrendData";
import { extendDSPMetrics } from "../utils/utils";
import useDSPSellerInfo from "./useDSPSellerInfo";

const useDSPCreativeData = (dimensions = "creative_id", includeMetadata = true, includeStats = true, filters) => {
  const { start, end, preStart, preEnd } = useDates();
  const { dspAdvertiser } = useDSPSellerInfo();

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  const formattedFilters = formatDatamartFilters({ ...filters, dsp_seller_info_id: dspAdvertiser?.id });

  const { data: dspCreativeEntityData, isLoading: isDSPCreativeEntityDataLoading } = useFetch(
    ["dspCreativeMetadata", startDate, endDate, dimensions, formattedFilters],
    "/api/data_report_v2/dspcreative/entities/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: startDate,
        date__lte: endDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => {
        return res.data?.map((order) => ({
          ...order,
          href: `/user/dsp/${dspAdvertiser?.id}/orders/${order.campaign_id}`,
        }));
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeMetadata,
    },
  );

  const { data: dspCreativeData, isLoading: isDSPCreativeDataLoading } = useFetch(
    ["dspCreativeData", startDate, endDate, dimensions, formattedFilters],
    "/api/data_report_v2/dspcreative/stats/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: startDate,
        date__lte: endDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => {
        return extendDSPMetrics(res.data);
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const { data: prevPeriodDSPCreativeData, isLoading: isPrevPeriodDSPCreativeDataLoading } = useFetch(
    ["prevPeriodDSPCreativeData", preStartDate, preEndDate, dimensions, formattedFilters],
    "/api/data_report_v2/dspcreative/stats/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: preStartDate,
        date__lte: preEndDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },

    {
      default: [],
      select: (res) => {
        return extendDSPMetrics(res.data);
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const mergedTrendData = useMemo(() => {
    return mergeTrendData(dspCreativeData, prevPeriodDSPCreativeData, "creative_id");
  }, [dspCreativeData, prevPeriodDSPCreativeData]);

  const isLoading = isDSPCreativeDataLoading || isDSPCreativeEntityDataLoading || isPrevPeriodDSPCreativeDataLoading;

  return { data: mergedTrendData, metadata: dspCreativeEntityData, isLoading };
};

export default useDSPCreativeData;
